/**
 * What the signed-in user is allowed to do.
 *
 * The list comes from `GET /users/me`, computed server-side from the same
 * registry the API enforces with. The browser is told its capabilities; it
 * never works them out. That is what stops the UI drifting from the API —
 * they are reading the same answer.
 *
 * It also fixes a staleness bug. The old checks read the role out of the JWT,
 * which is issued at login and lives eight hours, so a demoted user kept their
 * admin buttons until it expired and then got a 403 when they pressed one.
 * This is resolved from the database on every page load.
 *
 * Gating here is cosmetic. Every capability is enforced server-side too, and
 * a hidden button is never a reason to drop the check behind it.
 */
import React from 'react';
import { buildApiUrl } from '../config';

/** Keys mirror services/permissions/registry.py. */
export type Capability =
  | 'manage_members'
  | 'manage_roles'
  | 'manage_connectors'
  | 'build_data_model'
  | 'manage_data_model'
  | 'manage_knowledge'
  | 'manage_features'
  | 'manage_workspace'
  | 'manage_predictive_models'
  | 'use_copilot'
  | 'view_data_model'
  | 'view_connectors'
  | 'view_knowledge'
  | 'manage_own_work'
  | 'manage_agents';

interface PermissionsValue {
  can: (capability: Capability) => boolean;
  capabilities: Set<Capability>;
  roleName: string | null;
  userId: string | null;
  loading: boolean;
}

const PermissionsContext = React.createContext<PermissionsValue>({
  can: () => false,
  capabilities: new Set(),
  roleName: null,
  userId: null,
  loading: true,
});

function tenantId(): string | null {
  const raw = localStorage.getItem('klaris_tenant');
  if (!raw) return null;
  try {
    const t = JSON.parse(raw);
    return t.tenant_id || t.id || null;
  } catch {
    return raw;
  }
}

export function PermissionsProvider({ children }: { children: React.ReactNode }) {
  const [capabilities, setCapabilities] = React.useState<Set<Capability>>(new Set());
  const [roleName, setRoleName] = React.useState<string | null>(null);
  const [userId, setUserId] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  // Follow the token rather than reading it once. This provider sits above
  // the Router, so signing in and out never remounts it — without this, the
  // previous user's nav stayed on screen until a hard refresh. `auth-change`
  // is dispatched by login and by clearAuthStorage; `storage` covers the same
  // happening in another tab.
  const [token, setToken] = React.useState<string | null>(
    () => localStorage.getItem('klaris_jwt'),
  );

  React.useEffect(() => {
    const sync = () => setToken(localStorage.getItem('klaris_jwt'));
    window.addEventListener('auth-change', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('auth-change', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  React.useEffect(() => {
    const tid = tenantId();
    if (!tid || !token) {
      // Signed out: drop what the last session held.
      setCapabilities(new Set());
      setRoleName(null);
      setUserId(null);
      setLoading(false);
      return;
    }
    setLoading(true);

    let cancelled = false;
    fetch(buildApiUrl(`/api/v1/tenants/${tid}/users/me`), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        setCapabilities(new Set((data.capabilities || []) as Capability[]));
        setRoleName(data.role_name ?? null);
        setUserId(data.user_id ?? null);
      })
      // An unreachable API leaves the user with no capabilities, so the UI
      // shows its smallest form rather than buttons that will 403.
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [token]);

  const value = React.useMemo<PermissionsValue>(() => ({
    can: (capability: Capability) => capabilities.has(capability),
    capabilities,
    roleName,
    userId,
    loading,
  }), [capabilities, roleName, userId, loading]);

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions(): PermissionsValue {
  return React.useContext(PermissionsContext);
}

/** Shorthand for the common case. */
export function useCan(capability: Capability): boolean {
  return usePermissions().can(capability);
}
